/**
 * FieldHelp — a small info icon that opens a tooltip with explanatory text.
 *
 * Use next to a form-field label when the label alone isn't enough for
 * a new user to know what to type. Keep the text short — a sentence or
 * two; long explanations belong in the knowledge base.
 *
 * Usage:
 *
 *     <div className="flex items-center gap-2">
 *       <Label htmlFor="utm_source">Source</Label>
 *       <FieldHelp text="Where the click came from — e.g. 'newsletter', 'instagram'. Stays attached to every visit so you can split analytics by traffic source." />
 *     </div>
 *
 *     // Or pass rich content (paragraphs, lists) via children:
 *     <FieldHelp>
 *       <p>The slug is the part after the slash.</p>
 *       <p className="mt-1 text-muted-foreground">Use only letters, numbers, and dashes.</p>
 *     </FieldHelp>
 *
 * Notes:
 * - Wraps Radix's Tooltip — needs a TooltipProvider somewhere up the tree
 *   (we mount one in App.js so this works everywhere).
 * - The trigger is a `<button type="button">` so wrapping it inside a
 *   `<form>` doesn't accidentally submit on click.
 * - Marked `aria-label="More info"` so screen readers describe it sensibly.
 */

import React from 'react';
import { HelpCircle } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export function FieldHelp({
  text,
  children,
  side = 'top',
  align = 'center',
  className,
  iconClassName,
  ariaLabel = 'More info',
}) {
  const content = children ?? text;
  if (!content) return null;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
          data-testid="field-help-trigger"
        >
          <HelpCircle className={cn('h-3.5 w-3.5', iconClassName)} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        // The default tooltip class uses primary-on-dark; help text reads
        // better with a wider, slightly softer panel.
        className="max-w-xs whitespace-normal bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md border"
        data-testid="field-help-content"
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export default FieldHelp;
